import { FC, useEffect, useState } from 'react';
import { Box, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import CustomInput from '../../../../../common/CustomInput';
import CustomButton from '../../../../../common/CustomButton';

import styles from './SendReferralEmailModal.module.scss';

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

type SendReferralEmailModalProps = {
  open: boolean;
  onClose: () => void;
  onSend: (email: string) => Promise<void>;
  isSending: boolean;
};

const SendReferralEmailModal: FC<SendReferralEmailModalProps> = ({
  open,
  onClose,
  onSend,
  isSending,
}) => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setEmailError('');
      setSubmitError(null);
    }
  }, [open]);

  const validateEmail = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return 'Email is required';
    if (!EMAIL_REGEX.test(trimmed)) return 'Invalid email address';
    return '';
  };

  const handleClose = () => {
    if (isSending) return;
    onClose();
  };

  const handleSend = async () => {
    const validationError = validateEmail(email);
    setEmailError(validationError);
    setSubmitError(null);

    if (validationError) return;

    try {
      await onSend(email.trim());
    } catch {
      setSubmitError('Could not send the referral link. Please try again.');
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="send-referral-email-title"
      aria-describedby="send-referral-email-description"
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(34, 43, 58, 0.2)',
        },
      }}
    >
      <Box className={styles.sendModal}>
        <Box className={styles.sendModalClose}>
          <CloseIcon
            sx={{ width: 30, height: 30 }}
            onClick={handleClose}
          />
        </Box>

        <Box className={styles.sendModalContent}>
          <Typography
            id="send-referral-email-title"
            variant="h5"
            sx={{ fontWeight: 'bold', textAlign: 'center', color: '#222B3A' }}
          >
            Send referral link
          </Typography>
          <Typography
            id="send-referral-email-description"
            variant="body1"
            sx={{ color: '#222B3A', textAlign: 'center' }}
          >
            Enter an email address to send your referral link.
          </Typography>

          <CustomInput
            inputName="referralRecipientEmail"
            inputLabel="Email"
            inputType="email"
            value={email}
            onChange={e => {
              setEmail(e.target.value);
              if (emailError) setEmailError(validateEmail(e.target.value));
              if (submitError) setSubmitError(null);
            }}
            onBlur={() => setEmailError(validateEmail(email))}
            error={emailError}
            placeholder="email.example@gmail.com"
            customClass={styles.emailInput}
          />

          {submitError && (
            <Typography
              variant="h11"
              className={styles.submitError}
            >
              {submitError}
            </Typography>
          )}

          <Box className={styles.sendModalActions}>
            <CustomButton
              variant="outlined"
              onClick={handleClose}
              disabled={isSending}
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant="contained"
              onClick={handleSend}
              disabled={isSending}
              loading={isSending}
            >
              Send
            </CustomButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default SendReferralEmailModal;
